/**
 * RISKEE API Utilities
 * Shared API helpers and utility functions
 */

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Token storage key (must match useAuth.js)
const ACCESS_TOKEN_KEY = 'riskee_access_token';

// ============== UTILITY FUNCTIONS ==============

export const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(dateStr);
};

// ============== API HELPERS ==============

// Get auth headers
export const getAuthHeaders = () => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Handle 401 responses (session expired)
const handleResponse = async (res) => {
  if (res.status === 401) {
    // Clear auth and redirect to login
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem('riskee_refresh_token');
    localStorage.removeItem('riskee_user');
    window.location.reload();
    throw new Error('Session expired');
  }
  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: `API Error: ${res.status}` }));
    throw new Error(error.detail || `API Error: ${res.status}`);
  }
  return res.json();
};

// API client
export const api = {
  get: async (endpoint) => {
    const res = await fetch(`${API}${endpoint}`, {
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  },
  post: async (endpoint, data) => {
    const res = await fetch(`${API}${endpoint}`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },
  patch: async (endpoint, data) => {
    const res = await fetch(`${API}${endpoint}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    return handleResponse(res);
  },
  delete: async (endpoint) => {
    const res = await fetch(`${API}${endpoint}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return handleResponse(res);
  }
};

export default api;
